/**
 * Visual-only marketplace preview fixtures (no backend).
 * Used when the public API returns no stores and for /marketplace/store/:slug preview routes.
 */

export const PREVIEW_STORE_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "digital_art", label: "Digital Art" },
  { id: "game_assets", label: "Game Assets" },
  { id: "ai_tools", label: "AI Tools" },
  { id: "lore_comics", label: "Lore & Comics" },
  { id: "music_audio", label: "Music & Audio" },
  { id: "education", label: "Education" },
  { id: "fashion_merch", label: "Fashion & Merch" },
  { id: "vr_experiences", label: "VR Experiences" },
  { id: "services", label: "Services" },
];

/** @typedef {typeof PREVIEW_MARKETPLACE_STORES[number]} PreviewMarketplaceStore */

export const PREVIEW_MARKETPLACE_STORES = [
  {
    storeSlug: "koraxos-armory",
    storeName: "Koraxos Armory",
    district: "Military District",
    districtTheme: "Military forge corridor",
    propertyType: "Defense Annex",
    propertyClass: "Armory annex · hardened grid",
    categoryId: "game_assets",
    categoryLabel: "Game Assets",
    visualTone: "ember / bronze / military forge",
    heroTagline: "Forged loadouts for builders who fight in neon arenas.",
    shortDescription: "Weapon skins, tactical UI packs, and Koraxos-era props forged for competitive builders.",
    operatorName: "Quartermaster Jex",
    offerings: ["Ranked loadout kits", "HUD chrome packs", "Faction banners"],
    iconLabel: "‡",
    productSlotCount: 14,
    heroClass: "mp-sf-hero--ember",
    detailAccent: "ember",
    bannerVariant: "ember",
  },
  {
    storeSlug: "crystal-archive",
    storeName: "The Crystal Archive",
    district: "Archive District",
    districtTheme: "Crystal archive vault",
    propertyType: "Lore Vault",
    propertyClass: "Scholar vault · sealed stacks",
    categoryId: "lore_comics",
    categoryLabel: "Lore & Comics",
    visualTone: "violet / blue / crystal archive",
    heroTagline: "Chronicles and motion lore for readers who map timelines.",
    shortDescription: "Serialized Oshar chronicles, motion comics, and annotated world bibles for deep readers.",
    operatorName: "Archivist Seren",
    offerings: ["Motion comic arcs", "Codex annotations", "Timeline maps"],
    iconLabel: "◇",
    productSlotCount: 12,
    heroClass: "mp-sf-hero--violet",
    detailAccent: "violet",
    bannerVariant: "violet",
  },
  {
    storeSlug: "oshar-forge-ai-tools",
    storeName: "Oshar Forge AI Tools",
    district: "Tech Quarter",
    districtTheme: "Holographic command deck",
    propertyType: "Fabrication Lab",
    propertyClass: "Fabrication lab · holo stacks",
    categoryId: "ai_tools",
    categoryLabel: "AI Tools",
    visualTone: "electric cyan / holographic UI",
    heroTagline: "Assistants and macros tuned for Treydora creator teams.",
    shortDescription: "Prompt libraries, workflow automations, and creator-safe assistants tuned for Treydora teams.",
    operatorName: "Engineer Mire",
    offerings: ["Prompt spellbooks", "Pipeline macros", "Review copilots"],
    iconLabel: "⌁",
    productSlotCount: 16,
    heroClass: "mp-sf-hero--cyan",
    detailAccent: "cyan",
    bannerVariant: "cyan",
  },
  {
    storeSlug: "leviathan-gallery",
    storeName: "The Leviathan Gallery",
    district: "Waterfront District",
    districtTheme: "Waterfront exhibition pier",
    propertyType: "Exhibition Pier",
    propertyClass: "Gallery pier · saltlight glass",
    categoryId: "digital_art",
    categoryLabel: "Digital Art",
    visualTone: "aqua / silver / waterfront gallery",
    heroTagline: "Bioluminescent canvases and creature studies from Oshar.",
    shortDescription: "Bioluminescent canvases, creature studies, and limited holographic editions from Oshar artists.",
    operatorName: "Curator Vann",
    offerings: ["Limited editions", "Creature studies", "Holo prints"],
    iconLabel: "◆",
    productSlotCount: 11,
    heroClass: "mp-sf-hero--aqua",
    detailAccent: "aqua",
    bannerVariant: "aqua",
  },
  {
    storeSlug: "veyra-soundworks",
    storeName: "Veyra Soundworks",
    district: "Entertainment Row",
    districtTheme: "Entertainment auditorium loft",
    propertyType: "Auditorium Loft",
    propertyClass: "Auditorium loft · resonance shell",
    categoryId: "music_audio",
    categoryLabel: "Music & Audio",
    visualTone: "magenta / indigo / entertainment row",
    heroTagline: "Immersive stems and city ambiences for worlds and trailers.",
    shortDescription: "Immersive scores, diegetic ambiences, and VR-ready stems for worlds and trailers.",
    operatorName: "Maestro Veyra",
    offerings: ["VR-ready stems", "City ambience packs", "Trailer suites"],
    iconLabel: "♪",
    productSlotCount: 10,
    heroClass: "mp-sf-hero--magenta",
    detailAccent: "magenta",
    bannerVariant: "magenta",
  },
  {
    storeSlug: "treydora-academy-kiosk",
    storeName: "Treydora Academy Kiosk",
    district: "Knowledge District",
    districtTheme: "Academy scholar terminal",
    propertyType: "Scholars Hall",
    propertyClass: "Scholars hall · certification wing",
    categoryId: "education",
    categoryLabel: "Education",
    visualTone: "gold / academy terminal",
    heroTagline: "Micro-courses and cert prep aligned with Academy paths.",
    shortDescription: "Micro-courses, study templates, and certification prep aligned with Treydora Academy paths.",
    operatorName: "Provost Elian",
    offerings: ["Micro-courses", "Study templates", "Cert prep kits"],
    iconLabel: "∆",
    productSlotCount: 15,
    heroClass: "mp-sf-hero--gold",
    detailAccent: "gold",
    bannerVariant: "gold",
  },
  {
    storeSlug: "nok-merchant-house",
    storeName: "Nok Merchant House",
    district: "Merchant Bazaar",
    districtTheme: "Copper bazaar stall",
    propertyType: "Trade Stall",
    propertyClass: "Trade stall · season vault",
    categoryId: "fashion_merch",
    categoryLabel: "Fashion & Merch",
    visualTone: "copper / rose / bazaar",
    heroTagline: "Wearable lore and faction drops tied to Oshar seasons.",
    shortDescription: "Wearable lore, faction cloaks, and collectible merch drops tied to Oshar seasons.",
    operatorName: "Factor Nok",
    offerings: ["Faction cloaks", "Season pins", "Wearable lore"],
    iconLabel: "⬡",
    productSlotCount: 13,
    heroClass: "mp-sf-hero--rust",
    detailAccent: "rust",
    bannerVariant: "rust",
  },
  {
    storeSlug: "rift-experience-lab",
    storeName: "The Rift Experience Lab",
    district: "Portal District",
    districtTheme: "Portal rift laboratory",
    propertyType: "Rift Atrium",
    propertyClass: "Rift atrium · locomotion grid",
    categoryId: "vr_experiences",
    categoryLabel: "VR Experiences",
    visualTone: "portal blue / rift lab",
    heroTagline: "Prototype rooms and locomotion sandboxes for Treydora VR.",
    shortDescription: "Prototype spaces, locomotion sandboxes, and narrative rooms built for Treydora VR.",
    operatorName: "Lead Rigger Ayo",
    offerings: ["Locomotion labs", "Narrative rooms", "Lighting presets"],
    iconLabel: "⎔",
    productSlotCount: 9,
    heroClass: "mp-sf-hero--rift",
    detailAccent: "rift",
    bannerVariant: "rift",
  },
  {
    storeSlug: "citizen-services-bureau",
    storeName: "Citizen Services Bureau",
    district: "Civic District",
    districtTheme: "Civic services terminal",
    propertyType: "Civic Annex",
    propertyClass: "Civic annex · green steel desk",
    categoryId: "services",
    categoryLabel: "Services",
    visualTone: "green steel / civic terminal",
    heroTagline: "Consults and commission slots for verified citizens.",
    shortDescription: "Commission slots, onboarding consults, and white-glove build reviews for Oshar citizens.",
    operatorName: "Clerk Halden",
    offerings: ["Commission slots", "Onboarding consults", "Build reviews"],
    iconLabel: "⌘",
    productSlotCount: 8,
    heroClass: "mp-sf-hero--slate",
    detailAccent: "slate",
    bannerVariant: "slate",
  },
];

export function getPreviewStoreBySlug(slug) {
  if (!slug) return null;
  const key = String(slug).toLowerCase();
  return PREVIEW_MARKETPLACE_STORES.find((s) => s.storeSlug.toLowerCase() === key) || null;
}

export function filterPreviewStores(stores, categoryId) {
  if (!categoryId || categoryId === "all") return stores;
  return stores.filter((s) => s.categoryId === categoryId);
}
